// src/components/admin/belegOnboarding/BelegOnboarding.tsx
// ---------------------------------------------------------------------
// Drop-in onboarding flow for the Belege module. No external deps beyond
// the ones you already use (React + lucide-react + Tailwind with the
// brand-* tokens from your admin tailwind.config.js).
// ---------------------------------------------------------------------
import React, { useEffect, useMemo, useState } from 'react';
import {
  FileText, Receipt, RotateCcw, Check, ArrowRight, ArrowLeft, X,
  Send, Plus, Search, Calendar, Euro, Pencil, Sparkles, Download,
  HelpCircle,
} from 'lucide-react';

// ---------- props -----------------------------------------------------

interface Props {
  /** Controlled open state. */
  open: boolean;
  /** Called when the user dismisses (X, Esc, or "Überspringen"). */
  onClose: () => void;
  /** Optional starting chapter — useful for deep-links. */
  initialChapter?: 'angebot' | 'rechnung' | 'storno';
}

// ---------- shared mini-components ------------------------------------

const Pill: React.FC<{ children: React.ReactNode; tone?: 'gray' | 'blue' | 'green' | 'red' | 'yellow' | 'navy' }> = ({ children, tone = 'gray' }) => {
  const tones: Record<string, string> = {
    gray:   'bg-gray-100 text-gray-700',
    blue:   'bg-blue-100 text-blue-800',
    green:  'bg-emerald-100 text-emerald-800',
    red:    'bg-red-100 text-red-800',
    yellow: 'bg-amber-100 text-amber-800',
    navy:   'bg-brand-accent/10 text-brand-primary ring-1 ring-brand-accent/30',
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${tones[tone]}`}>{children}</span>;
};

const Btn: React.FC<{
  children: React.ReactNode;
  variant?: 'primary' | 'ghost' | 'outline' | 'danger';
  icon?: React.ReactNode;
  highlight?: boolean;
  className?: string;
}> = ({ children, variant = 'primary', icon, highlight, className = '' }) => {
  const v: Record<string, string> = {
    primary: 'bg-brand-primary text-white',
    ghost:   'text-brand-primary border border-brand-border bg-white',
    outline: 'text-brand-primary border border-brand-primary bg-white',
    danger:  'text-red-700 border border-red-200 bg-white',
  };
  return (
    <span className={`relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-semibold ${v[variant]} ${className}`}>
      {icon}{children}
      {highlight && <span className="absolute inset-0 rounded-md ring-2 ring-brand-accent pointer-events-none animate-pulse" />}
    </span>
  );
};

const Chip: React.FC<{ children: React.ReactNode; highlight?: boolean }> = ({ children, highlight }) => (
  <span className={`relative inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-gray-100 text-gray-800 border border-gray-200 ${highlight ? 'ring-2 ring-brand-accent' : ''}`}>
    {children}
  </span>
);

const Marker: React.FC<{ n: number; className?: string }> = ({ n, className = '' }) => (
  <span
    className={`absolute z-10 inline-flex items-center justify-center w-[22px] h-[22px] rounded-full bg-brand-primary text-white text-[11px] font-bold ring-4 ring-brand-primary/15 ${className}`}
  >
    {n}
  </span>
);

const Surface: React.FC<{
  title?: string; sub?: string; titleRight?: React.ReactNode;
  children: React.ReactNode; className?: string;
}> = ({ title, sub, titleRight, children, className = '' }) => (
  <div className={`bg-white rounded-xl border border-gray-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_4px_14px_-4px_rgba(15,23,42,0.08)] overflow-hidden ${className}`}>
    {title && (
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div>
          <div className="text-[13px] font-semibold text-gray-900">{title}</div>
          {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
        </div>
        {titleRight}
      </div>
    )}
    {children}
  </div>
);

const Row: React.FC<{ label: string; qty?: string; unit?: string; vat?: string; total?: string; muted?: boolean }> =
({ label, qty = '1', unit, vat = '0 %', total, muted }) => (
  <tr className={muted ? 'text-gray-400' : 'text-gray-800'}>
    <td className="py-1.5 pl-3 pr-2">{label}</td>
    <td className="py-1.5 px-2 tabular-nums text-right">{qty}</td>
    <td className="py-1.5 px-2 tabular-nums text-right">{unit}</td>
    <td className="py-1.5 px-2 tabular-nums text-right text-gray-500">{vat}</td>
    <td className="py-1.5 pl-2 pr-3 tabular-nums text-right font-semibold">{total}</td>
  </tr>
);

const IC = { w: 'w-4 h-4', s: 1.75 } as const;

// ---------- mocks (one per step) --------------------------------------

const Angebot1 = () => (
  <Surface
    title="Termin · Mi 27.05.2026"
    sub="10:00 — 11:30 · Dr. García · Sprechzimmer 2"
    titleRight={<Pill tone="yellow">scheduled</Pill>}
    className="max-w-[460px] mx-auto"
  >
    <div className="p-4 space-y-3">
      <div className="text-[12px] text-gray-500">Patient</div>
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-full bg-brand-accent/10 text-brand-primary text-[12px] font-semibold flex items-center justify-center">MP</div>
        <div>
          <div className="text-[13px] font-semibold text-gray-900">Maria Pérez</div>
          <div className="text-[11px] text-gray-500">maria.perez@example.com · ES</div>
        </div>
      </div>
      <div className="text-[12px] text-gray-500 pt-1">Behandlung</div>
      <div className="text-[13px] text-gray-800">Veneers · Beratung &amp; Planung</div>

      <div className="pt-3 -mx-4 px-4 border-t border-gray-100 flex flex-wrap gap-2">
        <Btn variant="ghost" icon={<Pencil className={IC.w} strokeWidth={IC.s} />}>Bearbeiten</Btn>
        <Btn variant="ghost" icon={<Check className={IC.w} strokeWidth={IC.s} />}>Bestätigen</Btn>
        <span className="relative">
          <Btn variant="outline" icon={<FileText className={IC.w} strokeWidth={IC.s} />} highlight>Angebot erstellen</Btn>
          <Marker n={1} className="-top-2 -right-2" />
        </span>
      </div>
    </div>
  </Surface>
);

const Angebot2 = () => (
  <Surface
    title="Neues Angebot · Entwurf"
    sub="Maria Pérez · gültig 30 Tage"
    titleRight={<Pill tone="gray">Entwurf</Pill>}
    className="max-w-[560px] mx-auto"
  >
    <div className="p-4 space-y-3">
      <div className="rounded-lg bg-brand-accent/10 border border-brand-accent/30 px-3 py-2 text-[11.5px] text-brand-primary">
        Nummer (AN-…) wird beim <strong>Festschreiben</strong> vergeben — solange Entwurf, frei editierbar.
      </div>
      <div className="rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-[11.5px]">
          <thead className="bg-gray-50 text-gray-500 text-[10.5px] uppercase tracking-wide">
            <tr>
              <th className="py-2 pl-3 text-left font-medium">Position</th>
              <th className="py-2 px-2 text-right font-medium">Menge</th>
              <th className="py-2 px-2 text-right font-medium">Einzel</th>
              <th className="py-2 px-2 text-right font-medium">MwSt</th>
              <th className="py-2 pl-2 pr-3 text-right font-medium">Gesamt</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <Row label="Veneer Keramik (pro Zahn)" qty="8" unit="650,00 €" total="5.200,00 €" />
            <Row label="Lokalanästhesie" qty="2" unit="25,00 €" total="50,00 €" vat="21 %" />
            <Row label="Diagnostik &amp; Wax-up" qty="1" unit="180,00 €" total="180,00 €" />
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-2 text-[11.5px] text-gray-600">
        <Btn variant="ghost" icon={<Plus className={IC.w} strokeWidth={IC.s} />}>Freie Position</Btn>
        <Btn variant="ghost" icon={<Search className={IC.w} strokeWidth={IC.s} />}>Aus Katalog</Btn>
      </div>
      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
        <div className="text-[11.5px] text-gray-500 space-y-0.5">
          <div className="flex justify-between"><span>Netto</span><span className="tabular-nums">5.380,00 €</span></div>
          <div className="flex justify-between"><span>MwSt 21 % auf 50 €</span><span className="tabular-nums">10,50 €</span></div>
          <div className="flex justify-between font-semibold text-gray-900 pt-1 border-t border-gray-100"><span>Total</span><span className="tabular-nums">5.440,50 €</span></div>
        </div>
        <div className="flex flex-col gap-1.5 items-end">
          <span className="relative">
            <Btn variant="primary" icon={<Check className={IC.w} strokeWidth={IC.s} />} highlight>Festschreiben</Btn>
            <Marker n={2} className="-top-2 -right-2" />
          </span>
          <Btn variant="ghost">Speichern</Btn>
        </div>
      </div>
    </div>
  </Surface>
);

const Angebot3 = () => (
  <Surface
    title="AN-0024 · Maria Pérez"
    sub="festgeschrieben · 27.05.2026 · gültig bis 26.06.2026"
    titleRight={<Pill tone="blue">festgeschrieben</Pill>}
    className="max-w-[560px] mx-auto"
  >
    <div className="p-4 space-y-3">
      <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-[11.5px] text-emerald-800 flex items-center gap-2">
        <Check className="w-4 h-4 text-emerald-600" strokeWidth={1.75} />
        Patient hat zugestimmt — Status „Angenommen" gesetzt.
      </div>
      <div className="grid grid-cols-[1fr_180px] gap-4">
        <div className="rounded-lg border border-gray-200 p-3 space-y-1.5 text-[11.5px] text-gray-700">
          <div className="font-medium text-gray-900">3 Positionen</div>
          <div className="text-gray-500">Veneers oben (8), Lokalanästhesie (2), Wax-up</div>
          <div className="pt-2 mt-2 border-t border-gray-100 flex items-center justify-between">
            <span className="text-gray-500">Gesamt</span>
            <span className="font-semibold text-gray-900 tabular-nums">5.440,50 €</span>
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Btn variant="ghost" icon={<Send className={IC.w} strokeWidth={IC.s} />}>Per Mail senden</Btn>
          <Btn variant="ghost" icon={<Check className={IC.w} strokeWidth={IC.s} />} className="text-emerald-700 border-emerald-200">Angebot angenommen</Btn>
          <span className="relative">
            <Btn variant="primary" icon={<ArrowRight className={IC.w} strokeWidth={IC.s} />} highlight className="w-full justify-center">In Rechnung umwandeln</Btn>
            <Marker n={3} className="-top-2 -right-2" />
          </span>
          <Btn variant="ghost" icon={<Download className={IC.w} strokeWidth={IC.s} />}>PDF</Btn>
        </div>
      </div>
    </div>
  </Surface>
);

const Rechnung1 = () => (
  <Surface
    title="Termin · Heute · 14:30"
    sub="Maria Pérez · 30 Min · Komposit-Füllung"
    titleRight={<Pill tone="green">confirmed</Pill>}
    className="max-w-[460px] mx-auto"
  >
    <div className="p-4 space-y-3">
      <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-[11.5px] text-gray-700">
        <div className="flex items-center gap-2 text-gray-500 mb-1">
          <Calendar className={IC.w} strokeWidth={IC.s} />
          <span>Termin-Detail</span>
        </div>
        Behandler: Dr. García · Raum 1 · Versicherung: privat
      </div>
      <div className="pt-2 border-t border-gray-100 flex flex-wrap gap-2">
        <Btn variant="ghost" icon={<Pencil className={IC.w} strokeWidth={IC.s} />}>Bearbeiten</Btn>
        <Btn variant="ghost" icon={<X className={IC.w} strokeWidth={IC.s} />}>Stornieren</Btn>
        <span className="relative">
          <Btn variant="primary" icon={<Check className={IC.w} strokeWidth={IC.s} />} highlight>Behandlung abschließen</Btn>
          <Marker n={1} className="-top-2 -right-2" />
        </span>
      </div>
      <div className="text-[10.5px] text-gray-500 pt-1">
        Ein Klick öffnet den Schnell-Dialog mit Quick-Chips für die häufigsten Leistungen.
      </div>
    </div>
  </Surface>
);

const Rechnung2 = () => (
  <Surface
    title="Behandlung abschließen — Maria Pérez"
    sub="Termin: Heute · 14:30"
    titleRight={<Pill tone="navy">Schnell-Dialog</Pill>}
    className="max-w-[600px] mx-auto"
  >
    <div className="p-4 space-y-3">
      <div className="space-y-1.5">
        <div className="text-[10.5px] uppercase tracking-wide text-gray-500 font-medium">Schnellauswahl</div>
        <div className="flex flex-wrap gap-1.5 relative">
          <Chip>Untersuchung · 50 €</Chip>
          <Chip highlight>Komposit-Füllung · 120 €</Chip>
          <Chip>Lokalanästhesie · 25 €</Chip>
          <Chip>Röntgen · 35 €</Chip>
          <Chip>Zahnreinigung · 80 €</Chip>
          <Chip>Beratung · 40 €</Chip>
          <Marker n={1} className="-top-2 left-[152px]" />
        </div>
        <div className="text-[10.5px] text-gray-500">Klick = Position hinzufügen · erneuter Klick erhöht die Menge.</div>
      </div>

      <div className="rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-[11.5px]">
          <thead className="bg-gray-50 text-gray-500 text-[10.5px] uppercase tracking-wide">
            <tr>
              <th className="py-2 pl-3 text-left font-medium">Position</th>
              <th className="py-2 px-2 text-right font-medium">Menge</th>
              <th className="py-2 px-2 text-right font-medium">Einzel</th>
              <th className="py-2 px-2 text-right font-medium">MwSt</th>
              <th className="py-2 pl-2 pr-3 text-right font-medium">Gesamt</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <Row label="Komposit-Füllung Zahn 26" qty="1" unit="120,00 €" total="120,00 €" />
            <Row label="Lokalanästhesie" qty="1" unit="25,00 €" vat="21 %" total="30,25 €" />
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-2 relative">
        <Btn variant="ghost" icon={<Search className={IC.w} strokeWidth={IC.s} />}>Aus Katalog suchen</Btn>
        <Btn variant="ghost" icon={<Plus className={IC.w} strokeWidth={IC.s} />}>Freie Position</Btn>
        <Marker n={2} className="-top-2 left-[148px]" />
        <div className="ml-auto text-[12px] text-gray-500">
          Gesamt <span className="text-brand-primary font-semibold tabular-nums ml-1">150,25 €</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100">
        <Btn variant="ghost">Als Entwurf</Btn>
        <Btn variant="ghost">Ohne Rechnung</Btn>
        <Btn variant="ghost">Abbrechen</Btn>
        <span className="relative">
          <Btn variant="primary" icon={<Check className={IC.w} strokeWidth={IC.s} />} highlight className="w-full justify-center">Direkt ausstellen</Btn>
          <Marker n={3} className="-top-2 -right-2" />
        </span>
      </div>
    </div>
  </Surface>
);

const Rechnung3 = () => (
  <Surface
    title="RE-0143 · Maria Pérez"
    sub="festgeschrieben · 15.05.2026 · fällig 29.05.2026"
    titleRight={<Pill tone="blue">festgeschrieben</Pill>}
    className="max-w-[560px] mx-auto"
  >
    <div className="p-4 space-y-3">
      <div className="grid grid-cols-[1fr_200px] gap-4">
        <div className="rounded-lg border border-gray-200 p-3 space-y-1.5 text-[11.5px] text-gray-700">
          <div className="flex items-center justify-between">
            <span className="font-medium text-gray-900">Beleg-PDF</span>
            <Pill tone="gray">DE</Pill>
          </div>
          <div className="h-28 rounded border border-gray-100 bg-[linear-gradient(rgba(15,23,42,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(15,23,42,0.04)_1px,transparent_1px)] bg-[length:24px_24px] flex items-center justify-center text-[10.5px] text-gray-400">
            PDF-Vorschau
          </div>
          <div className="flex items-center justify-between pt-1">
            <span className="text-gray-500">Total</span>
            <span className="font-semibold text-gray-900 tabular-nums">150,25 €</span>
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="relative">
            <Btn variant="primary" icon={<Send className={IC.w} strokeWidth={IC.s} />} highlight className="w-full justify-center">Per Mail senden</Btn>
            <Marker n={1} className="-top-2 -right-2" />
          </span>
          <Btn variant="ghost" icon={<Download className={IC.w} strokeWidth={IC.s} />}>PDF</Btn>
          <Btn variant="ghost" icon={<Euro className={IC.w} strokeWidth={IC.s} />}>Als bezahlt markieren</Btn>
          <Btn variant="ghost" icon={<X className={IC.w} strokeWidth={IC.s} />} className="text-red-700 border-red-200">Stornieren</Btn>
        </div>
      </div>

      <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-[11.5px] text-gray-700 relative">
        <div className="font-semibold text-gray-900 mb-1 flex items-center gap-1">
          <Send className={IC.w} strokeWidth={IC.s} /><span>Versand-Dialog</span>
        </div>
        <div className="text-gray-600">An: <span className="text-gray-900">maria.perez@example.com</span> · Sprache: ES · Betreff &amp; Anschreiben aus Sprach-Vorlage, direkt im Dialog editierbar.</div>
        <Marker n={2} className="-top-2 -left-2" />
      </div>
    </div>
  </Surface>
);

const Storno1 = () => (
  <Surface
    title="RE-0142 · Carlos Méndez"
    sub="festgeschrieben · 12.05.2026"
    titleRight={<Pill tone="blue">festgeschrieben</Pill>}
    className="max-w-[560px] mx-auto"
  >
    <div className="p-4 space-y-3">
      <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[11.5px] text-amber-900">
        <strong>Festgeschrieben</strong> — der Beleg ist gesetzlich nicht mehr editierbar. Korrekturen ausschließlich über Stornierung.
      </div>
      <div className="rounded-lg border border-gray-200 overflow-hidden opacity-70">
        <table className="w-full text-[11.5px]">
          <tbody className="divide-y divide-gray-100">
            <Row label="Wurzelkanalbehandlung Zahn 36" qty="1" unit="780,00 €" total="780,00 €" muted />
            <Row label="Röntgen 2D" qty="2" unit="35,00 €" total="70,00 €" vat="21 %" muted />
            <Row label="Lokalanästhesie" qty="2" unit="25,00 €" total="50,00 €" vat="21 %" muted />
          </tbody>
        </table>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Btn variant="ghost" icon={<Send className={IC.w} strokeWidth={IC.s} />}>Per Mail senden</Btn>
        <span className="relative">
          <Btn variant="danger" icon={<X className={IC.w} strokeWidth={IC.s} />} highlight className="w-full justify-center">Stornieren</Btn>
          <Marker n={1} className="-top-2 -right-2" />
        </span>
      </div>
    </div>
  </Surface>
);

const Storno2 = () => (
  <Surface title="Rechnung stornieren?" sub="RE-0142 · Carlos Méndez · 900,50 €" className="max-w-[460px] mx-auto">
    <div className="p-4 space-y-3">
      <div className="text-[12px] text-gray-700">
        Es wird automatisch eine <strong>Stornorechnung (ST-…)</strong> mit eigener Nummer und negativen Beträgen erzeugt. Die Originalrechnung erhält den Status „storniert".
      </div>
      <div className="relative">
        <label className="text-[10.5px] uppercase tracking-wide text-gray-500 font-medium">Grund (Pflicht)</label>
        <textarea
          readOnly rows={2}
          defaultValue="Falscher Patient zugeordnet — Position wird auf RE-0144 neu ausgestellt."
          className="mt-1 w-full text-[12px] px-3 py-2 rounded-md border border-gray-300 bg-white text-gray-900 focus:outline-none ring-2 ring-brand-accent/40"
        />
        <Marker n={2} className="-top-2 -left-2" />
      </div>
      <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
        <Btn variant="ghost">Abbrechen</Btn>
        <Btn variant="danger" icon={<X className={IC.w} strokeWidth={IC.s} />}>Stornieren</Btn>
      </div>
    </div>
  </Surface>
);

const Storno3 = () => (
  <Surface
    title="Belege · Carlos Méndez"
    sub="Letzte 30 Tage"
    titleRight={<Pill tone="navy">Patientenakte</Pill>}
    className="max-w-[600px] mx-auto"
  >
    <div className="p-1">
      <table className="w-full text-[11.5px]">
        <thead className="bg-gray-50 text-gray-500 text-[10.5px] uppercase tracking-wide">
          <tr>
            <th className="py-2 pl-3 text-left font-medium">Nummer</th>
            <th className="py-2 px-2 text-left font-medium">Datum</th>
            <th className="py-2 px-2 text-left font-medium">Status</th>
            <th className="py-2 pl-2 pr-3 text-right font-medium">Betrag</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          <tr className="text-gray-500">
            <td className="py-2 pl-3 font-medium tabular-nums line-through">RE-0142</td>
            <td className="py-2 px-2">12.05.</td>
            <td className="py-2 px-2"><Pill tone="red">storniert</Pill></td>
            <td className="py-2 pl-2 pr-3 tabular-nums line-through">900,50 €</td>
          </tr>
          <tr className="bg-brand-accent/5">
            <td className="py-2 pl-3 font-semibold tabular-nums text-brand-primary">ST-0007</td>
            <td className="py-2 px-2 text-gray-700">15.05.</td>
            <td className="py-2 px-2"><Pill tone="navy">Stornorechnung</Pill></td>
            <td className="py-2 pl-2 pr-3 tabular-nums font-semibold text-red-700">−900,50 €</td>
          </tr>
          <tr>
            <td className="py-2 pl-3 font-medium tabular-nums text-gray-800">RE-0144</td>
            <td className="py-2 px-2 text-gray-700">15.05.</td>
            <td className="py-2 px-2"><Pill tone="green">korrigierte Rechnung</Pill></td>
            <td className="py-2 pl-2 pr-3 tabular-nums font-semibold">820,00 €</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div className="px-4 py-3 border-t border-gray-100 bg-emerald-50/40 text-[11.5px] text-emerald-900 flex items-start gap-2 relative">
      <Check className="w-4 h-4 text-emerald-600 mt-0.5" strokeWidth={1.75} />
      <div>
        <div className="font-semibold">Nummernkreis bleibt lückenlos.</div>
        Original, Storno und Neu-Ausstellung tauchen alle drei in der Belegliste auf — so akzeptiert es das Finanzamt.
      </div>
      <Marker n={3} className="-top-2 -left-2" />
    </div>
  </Surface>
);

// ---------- chapter content ------------------------------------------

type Annotation = { n: number; text: string };
type Step = { title: string; body: string; tip?: string; annotations: Annotation[]; mock: React.FC };
type Chapter = {
  id: 'angebot' | 'rechnung' | 'storno';
  tag: string;
  title: string;
  blurb: string;
  mins: string;
  color: string;
  iconBg: string;
  icon: React.ReactNode;
  steps: Step[];
};

const CHAPTERS: Chapter[] = [
  {
    id: 'angebot', tag: 'AN-…', title: 'Angebot erstellen', mins: '~2 min',
    blurb: 'Kostenvoranschlag vor der Behandlung — versenden, vom Patienten bestätigen lassen, in eine Rechnung umwandeln.',
    color: 'from-indigo-50 to-blue-50',
    iconBg: 'bg-indigo-50 text-indigo-700',
    icon: <FileText className="w-5 h-5" strokeWidth={1.75} />,
    steps: [
      { mock: Angebot1, title: 'Vom zukünftigen Termin aus starten',
        body: 'Öffne den Termin im Kalender und klicke unten auf „Angebot erstellen". Der Editor übernimmt Patient, Sprache und Anschrift automatisch.',
        tip: 'Tipp: Geht auch aus der Patientenakte heraus über „+ Beleg → Angebot".',
        annotations: [{ n: 1, text: '„Angebot erstellen" öffnet den Editor mit allen Patientendaten vorbefüllt.' }] },
      { mock: Angebot2, title: 'Positionen erfassen und festschreiben',
        body: 'Trag die geplanten Leistungen ein — über „Aus Katalog" für Standards oder „Freie Position" für Sonderfälle. Mit Festschreiben bekommt das Angebot seine endgültige Nummer und das PDF wird erzeugt.',
        tip: 'Solange Entwurf, kannst du beliebig editieren oder verwerfen — keine Lücke in der Nummernreihe.',
        annotations: [{ n: 2, text: 'Festschreiben vergibt die Nummer und sperrt den Beleg gegen Änderungen.' }] },
      { mock: Angebot3, title: 'Annehmen lassen → in Rechnung umwandeln',
        body: 'Versende das PDF per Mail. Sagt der Patient zu, klick auf „Angebot angenommen" und dann auf „In Rechnung umwandeln". Alle Positionen wandern in einen neuen Rechnungsentwurf.',
        tip: 'Der Angebot-Verlauf bleibt am Termin sichtbar (Tab „Verlauf").',
        annotations: [{ n: 3, text: '„In Rechnung umwandeln" — ein Klick, Positionen werden 1:1 übernommen.' }] },
    ],
  },
  {
    id: 'rechnung', tag: 'RE-…', title: 'Rechnung schreiben', mins: '~2 min',
    blurb: 'Der Hauptpfad nach jeder Behandlung. Termin abschließen, Positionen festklicken, ausstellen, per Mail senden.',
    color: 'from-emerald-50 to-teal-50',
    iconBg: 'bg-emerald-50 text-emerald-700',
    icon: <Receipt className="w-5 h-5" strokeWidth={1.75} />,
    steps: [
      { mock: Rechnung1, title: 'Termin abschließen am Behandlungstag',
        body: 'Im Kalender den Termin öffnen → „Behandlung abschließen" klicken. Das ist der Schnell-Dialog — der einzige Knopf, den du im Alltag brauchst.',
        tip: 'Aus der Appointments-Liste heraus geht es genauso — Link „Open in Calendar".',
        annotations: [{ n: 1, text: 'Die Termin-Behandlung steht im Dialog schon als erste Position drin.' }] },
      { mock: Rechnung2, title: 'Schnellauswahl-Chips & Katalog',
        body: 'Klick die Quick-Chips für häufige Leistungen — jeder Klick fügt eine Zeile hinzu (zweiter Klick erhöht die Menge). Spezielles holst du dir mit „Aus Katalog suchen", einmalige Leistungen über „Freie Position".',
        tip: 'Welche Chips erscheinen, stellst du in „Einstellungen → Rechnungsstellung → Schnellauswahl" ein (max. 8).',
        annotations: [
          { n: 1, text: 'Quick-Chip klicken = Position hinzufügen.' },
          { n: 2, text: 'Katalog für seltenere Leistungen · Freie Position für Sonderfälle.' },
          { n: 3, text: '„Direkt ausstellen" = Termin abschließen + Rechnung festschreiben + PDF — in einem Schritt.' },
        ] },
      { mock: Rechnung3, title: 'Per Mail senden',
        body: 'Nach „Direkt ausstellen" springt die Ansicht in den fertigen Beleg. Klick „Per Mail senden" — Empfänger und Sprache sind vorbefüllt, Betreff und Anschreiben kommen aus der Vorlage und sind im Dialog editierbar.',
        tip: 'Variablen wie {{number}}, {{patient_name}}, {{total}} werden beim Versand ersetzt.',
        annotations: [
          { n: 1, text: 'Mail-Erfolg/-Fehler landet automatisch im Verlauf-Tab des Termins.' },
          { n: 2, text: 'Vorlage pro Sprache (DE/ES/EN) — pro Mail individuell anpassbar.' },
        ] },
    ],
  },
  {
    id: 'storno', tag: 'ST-…', title: 'Stornorechnung', mins: '~2 min',
    blurb: 'Festgeschriebene Rechnungen lassen sich nicht editieren — Korrekturen laufen über die Storno. Klingt streng, ist aber Pflicht.',
    color: 'from-rose-50 to-orange-50',
    iconBg: 'bg-rose-50 text-rose-700',
    icon: <RotateCcw className="w-5 h-5" strokeWidth={1.75} />,
    steps: [
      { mock: Storno1, title: 'Falsche Rechnung öffnen',
        body: 'Öffne die zu stornierende Rechnung. Sie ist gegen Änderungen gesperrt — ein gelber Hinweis erklärt das. Im Aktionspanel rechts findest du den Stornieren-Knopf.',
        tip: 'Eine festgeschriebene Rechnung wird nie gelöscht — der Originalbeleg bleibt für die Buchhaltung erhalten.',
        annotations: [{ n: 1, text: '„Stornieren" startet die Korrektur. Der Originalbeleg bleibt unverändert bestehen.' }] },
      { mock: Storno2, title: 'Grund eintragen',
        body: 'Schreib einen kurzen, ehrlichen Grund (z. B. „falscher Patient", „falscher MwSt-Satz", „Doppelt erfasst"). Der Grund landet im Audit-Log und auf dem Storno-PDF.',
        tip: 'Pflichtfeld — ohne Grund lässt sich nicht stornieren.',
        annotations: [{ n: 2, text: 'Der Grund erscheint später auf dem Storno-PDF und in der Belegliste.' }] },
      { mock: Storno3, title: 'Storno + neue Rechnung',
        body: 'Beim Bestätigen entsteht automatisch eine Stornorechnung (ST-…) mit negativen Beträgen und eigener Nummer. Anschließend bei Bedarf eine korrigierte Rechnung neu ausstellen.',
        tip: 'Alle drei Belege (Original, Storno, Korrektur) bleiben in der Belegliste sichtbar.',
        annotations: [{ n: 3, text: 'Das Finanzamt erwartet genau diese drei Zeilen. Nichts überschreiben, nichts löschen.' }] },
    ],
  },
];

// ---------- progress -------------------------------------------------

const STORAGE_KEY = 'seadent-help-belege-v1';
type Progress = Record<string, { step?: number; completed?: boolean }>;
const loadProgress = (): Progress => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { return {}; }
};

// ---------- views ----------------------------------------------------

const Hub: React.FC<{ progress: Progress; onPick: (id: Chapter['id']) => void; onClose: () => void }> = ({ progress, onPick, onClose }) => {
  const done = CHAPTERS.filter(c => progress[c.id]?.completed).length;
  return (
    <div className="animate-[fadeIn_0.3s_ease-out]">
      <div className="px-8 pt-8 pb-6 border-b border-gray-100">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-brand-primary font-semibold">
          <Sparkles className="w-4 h-4" strokeWidth={1.75} /><span>Hilfe — Erste Schritte</span>
        </div>
        <h1 className="mt-2 text-[28px] font-bold text-gray-900 leading-tight">Belege schreiben in SeaDent</h1>
        <p className="mt-1.5 text-[14px] text-gray-600 max-w-2xl">
          Drei kurze Touren — Angebot, Rechnung und Stornorechnung. Jede dauert keine zwei Minuten.
          Du kannst sie in beliebiger Reihenfolge machen oder jederzeit anhalten und später fortfahren.
        </p>
        <div className="mt-4 flex items-center gap-3">
          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden max-w-xs">
            <div className="h-full bg-brand-primary transition-all" style={{ width: `${(done / CHAPTERS.length) * 100}%` }} />
          </div>
          <div className="text-[11.5px] text-gray-500 font-medium tabular-nums">{done}/{CHAPTERS.length} abgeschlossen</div>
        </div>
      </div>

      <div className="px-8 py-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        {CHAPTERS.map(ch => {
          const completed = progress[ch.id]?.completed;
          const started = (progress[ch.id]?.step ?? 0) > 0 && !completed;
          return (
            <button
              key={ch.id}
              onClick={() => onPick(ch.id)}
              className={`group text-left rounded-xl border border-gray-200 bg-gradient-to-br ${ch.color} p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all relative overflow-hidden cursor-pointer`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${ch.iconBg}`}>{ch.icon}</div>
                <span className="text-[10.5px] font-mono font-semibold text-gray-500 bg-white/70 px-2 py-0.5 rounded">{ch.tag}</span>
              </div>
              <div className="text-[16px] font-bold text-gray-900 mb-1">{ch.title}</div>
              <div className="text-[12.5px] text-gray-700 leading-snug">{ch.blurb}</div>
              <div className="mt-4 flex items-center justify-between text-[11px]">
                <span className="text-gray-500">{ch.steps.length} Schritte · {ch.mins}</span>
                {completed && <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><Check className="w-3.5 h-3.5" strokeWidth={2} /> fertig</span>}
                {started && <span className="inline-flex items-center gap-1 text-brand-primary font-semibold">fortsetzen <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} /></span>}
                {!started && !completed && <span className="inline-flex items-center gap-1 text-brand-primary font-semibold opacity-0 group-hover:opacity-100 transition-opacity">starten <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} /></span>}
              </div>
            </button>
          );
        })}
      </div>

      <div className="px-8 pb-7 flex items-center justify-between text-[11.5px] text-gray-500 border-t border-gray-100 pt-5">
        <div>Diese Hilfe findest du jederzeit unter <span className="text-brand-primary font-medium">Hilfe → Belege</span>.</div>
        <button onClick={onClose} className="text-brand-primary hover:underline font-medium cursor-pointer">Überspringen</button>
      </div>
    </div>
  );
};

const ChapterView: React.FC<{
  chapter: Chapter; step: number;
  onStep: (n: number) => void; onBack: () => void; onComplete: () => void;
}> = ({ chapter, step, onStep, onBack, onComplete }) => {
  const s = chapter.steps[step];
  const Mock = s.mock;
  const total = chapter.steps.length;

  return (
    <div className="animate-[fadeIn_0.3s_ease-out]">
      <div className="px-8 pt-6 pb-4 border-b border-gray-100 flex items-center gap-3">
        <button onClick={onBack} className="inline-flex items-center gap-1 text-[12px] text-gray-500 hover:text-brand-primary font-medium transition-colors cursor-pointer">
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} /><span>Übersicht</span>
        </button>
        <span className="text-gray-300">/</span>
        <div className={`h-7 w-7 rounded-md flex items-center justify-center ${chapter.iconBg}`}>{React.cloneElement(chapter.icon as React.ReactElement<{ className?: string }>, { className: 'w-4 h-4' })}</div>
        <div className="text-[13px] font-semibold text-gray-900">{chapter.title}</div>
        <div className="ml-auto flex items-center gap-1.5">
          {chapter.steps.map((_, i) => (
            <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? 'w-8 bg-brand-primary' : i < step ? 'w-4 bg-brand-primary/40' : 'w-4 bg-gray-200'}`} />
          ))}
          <span className="ml-2 text-[11px] text-gray-500 tabular-nums">{step + 1} / {total}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-0">
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 px-6 py-8 border-r border-gray-100 flex items-center justify-center min-h-[420px] bg-[linear-gradient(rgba(15,23,42,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(15,23,42,0.04)_1px,transparent_1px)] bg-[length:24px_24px]">
          <div key={`${chapter.id}-${step}`} className="w-full"><Mock /></div>
        </div>
        <div className="px-8 py-8 flex flex-col">
          <div className="text-[11px] font-mono uppercase tracking-wider text-brand-primary mb-2">Schritt {step + 1} von {total}</div>
          <h2 className="text-[22px] font-bold text-gray-900 leading-tight">{s.title}</h2>
          <p className="mt-3 text-[14px] text-gray-700 leading-relaxed">{s.body}</p>
          <div className="mt-5 space-y-2">
            {s.annotations.map(a => (
              <div key={a.n} className="flex items-start gap-2.5 text-[12.5px] text-gray-700 leading-snug">
                <span className="mt-0.5 shrink-0 inline-flex items-center justify-center w-[22px] h-[22px] rounded-full bg-brand-primary text-white text-[11px] font-bold">{a.n}</span>
                <span>{a.text}</span>
              </div>
            ))}
          </div>
          {s.tip && (
            <div className="mt-5 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-[12px] text-amber-900 flex items-start gap-2">
              <Sparkles className="w-3.5 h-3.5 text-amber-600 mt-0.5" strokeWidth={1.75} /><span>{s.tip}</span>
            </div>
          )}
          <div className="mt-auto pt-8 flex items-center justify-between gap-3">
            <button disabled={step === 0} onClick={() => onStep(step - 1)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[13px] font-semibold text-gray-700 border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer">
              <ArrowLeft className="w-4 h-4" strokeWidth={1.75} /><span>Zurück</span>
            </button>
            {step < total - 1 ? (
              <button onClick={() => onStep(step + 1)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover transition-colors shadow-sm cursor-pointer">
                <span>Weiter</span><ArrowRight className="w-4 h-4" strokeWidth={1.75} />
              </button>
            ) : (
              <button onClick={onComplete} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 transition-colors shadow-sm cursor-pointer">
                <Check className="w-4 h-4" strokeWidth={2} /><span>Tour abschließen</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- public component -----------------------------------------

export const BelegOnboarding: React.FC<Props> = ({ open, onClose, initialChapter }) => {
  const [progress, setProgress] = useState<Progress>(loadProgress);
  const [active, setActive] = useState<Chapter['id'] | null>(initialChapter ?? null);
  const [step, setStep] = useState(0);

  useEffect(() => { try { localStorage.setItem(STORAGE_KEY, JSON.stringify(progress)); } catch {} }, [progress]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const chapter = useMemo(() => CHAPTERS.find(c => c.id === active) || null, [active]);

  if (!open) return null;

  const enterChapter = (id: Chapter['id']) => {
    const saved = progress[id]?.step ?? 0;
    const ch = CHAPTERS.find(c => c.id === id)!;
    setActive(id);
    setStep(saved < ch.steps.length ? saved : 0);
  };
  const goStep = (n: number) => {
    setStep(n);
    setProgress(p => ({ ...p, [active!]: { ...(p[active!] || {}), step: n } }));
  };
  const complete = () => {
    setProgress(p => ({ ...p, [active!]: { step: chapter!.steps.length, completed: true } }));
    setActive(null);
    setStep(0);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-black/40 backdrop-blur-sm">
      <div className="relative w-full max-w-[1080px] max-h-[92vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-gray-200">
        <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-brand-primary to-brand-primary-hover sticky top-0 z-10">
          <div className="flex items-center gap-2 text-white text-[12px] font-semibold tracking-wide">
            <span className="h-6 w-6 rounded-md bg-white/20 flex items-center justify-center">
              <HelpCircle className="w-3.5 h-3.5" strokeWidth={1.75} />
            </span>
            SeaDent · Hilfe-Tour
          </div>
          <button onClick={onClose} className="inline-flex items-center justify-center h-7 w-7 rounded-md text-white/80 hover:text-white hover:bg-white/15 transition-colors cursor-pointer" aria-label="Schließen">
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        {active === null
          ? <Hub progress={progress} onPick={enterChapter} onClose={onClose} />
          : <ChapterView chapter={chapter!} step={step} onStep={goStep} onBack={() => { setActive(null); setStep(0); }} onComplete={complete} />}
      </div>
    </div>
  );
};

export default BelegOnboarding;
